import {
  CFormCheck,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import PromptModal from "../../components/modals/PromptModal";
import { HeaderSort } from "../../components/ui/TableHeaderSort";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { Chip, formatDateString } from "../../helpers/general";
import { useUserQuery } from "../../hooks/users/useUserQuery";
import { tdStyle, thStyle } from "../../helpers/constants";

function SortTh({
  label,
  field,
  sort,
}: {
  label: string;
  field?: string;
  sort?: TableProps["sort"];
}) {
  return (
    <CTableHeaderCell scope="col" style={thStyle}>
      <div className="d-flex align-items-center gap-1">
        {label}
        {field && sort && (
          <HeaderSort
            field={field}
            sort={sort.sort.order}
            sortedField={sort.sort}
            setSort={(f, order) => sort.setSort({ field: f, order })}
            clearSort={() => sort.resetSort()}
            defaultField="createdAt"
          />
        )}
      </div>
    </CTableHeaderCell>
  );
}

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  user,
  deleteList,
}: {
  user: User;
  deleteList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { removeUser } = useUserQuery();

  useEffect(() => {
    if (isChecked) {
      deleteList({ type: DELETE_ACTIONS.ADD, id: user.id });
    } else {
      deleteList({ type: DELETE_ACTIONS.REMOVE, id: user.id });
    }
  }, [deleteList, isChecked, user.id]);

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Registration date */}
      <CTableDataCell style={tdStyle}>
        <div className="fw-semibold">{formatDateString(user.createdAt)}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {format(new Date(user.createdAt), "HH:mm")}
        </div>
      </CTableDataCell>

      {/* User */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 200 }}
        >
          <img
            src={
              user.profilePicture ??
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                user?.fullName ?? "",
              )}`
            }
            alt=""
            width={36}
            height={36}
            className="flex-shrink-0"
            style={{ borderRadius: "50%", objectFit: "cover" }}
          />
          <div style={{ minWidth: 0 }}>
            <Link
              to={`/users/${user.id}`}
              className="fw-semibold text-capitalize d-block text-truncate"
              style={{ maxWidth: 240 }}
            >
              {user?.fullName}
            </Link>
            <div
              className="text-muted text-truncate"
              style={{ fontSize: 12, maxWidth: 240 }}
            >
              {user.email}
            </div>
          </div>
        </div>
      </CTableDataCell>

      {/* Phone */}
      <CTableDataCell style={tdStyle}>
        {user?.phone ? (
          <span style={{ whiteSpace: "nowrap" }}>{user.phone}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Verified */}
      <CTableDataCell style={tdStyle}>
        {user?.isVerifiedPhone ? (
          <Chip tone="success">✓ Vérifié</Chip>
        ) : (
          <Chip tone="danger">✕ Non vérifié</Chip>
        )}
      </CTableDataCell>

      {/* Birthday */}
      <CTableDataCell style={tdStyle}>
        {user?.birthday ? (
          formatDateString(user.birthday)
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* City */}
      <CTableDataCell style={tdStyle}>
        {user?.city?.label ? (
          <Chip>{user.city.label}</Chip>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex justify-content-end">
          <button
            onClick={() => setShowModal(true)}
            title="Supprimer"
            aria-label="Supprimer"
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{
              width: 34,
              height: 34,
              backgroundColor: "rgba(220,53,69,0.10)",
            }}
          >
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={() => removeUser(user?.id)}
        onClose={() => setShowModal(false)}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  users: User[];
  UsersToBeDeletedList: React.Dispatch<BulkDeleteActions>;
  sort: {
    sort: { field: string; order: string };
    setSort: ({ field, order }: { field: string; order: string }) => void;
    resetSort: () => void;
  };
}

export default function UsersTable({
  users,
  UsersToBeDeletedList,
  sort,
}: TableProps) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <SortTh label="Inscription" field="createdAt" sort={sort} />
            <SortTh label="Utilisateur" field="fullName" sort={sort} />
            <SortTh label="Téléphone" field="phone" sort={sort} />
            <SortTh label="Vérification" field="isVerifiedPhone" sort={sort} />
            <SortTh label="Naissance" field="birthday" sort={sort} />
            <SortTh label="Ville" />
            <CTableHeaderCell style={thStyle} />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {users?.map((user) => (
            <TableBodyRowContent
              key={user.id}
              user={user}
              deleteList={UsersToBeDeletedList}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
